import type { AnyScreenDefinition } from './types.js';
import { SCREEN_MODULES } from './index.generated.js';

/**
 * The screen registry.
 *
 * The list of screens is generated (see packages/web/src/cli/generate-screen-index.ts)
 * rather than discovered at runtime, because the browser and Node cannot
 * discover it the same way: Node can read the directory, a bundle cannot.
 * Two discovery mechanisms would be two answers to "which screens exist", and
 * the one that disagreed would be the one running in production. So the
 * directory is read once, at generation time, and both sides import the result.
 *
 * check:screens compares the generated index against the directory and fails
 * when they differ, which is what keeps the generated file honest.
 */

export interface ScreenRegistry {
  screens: AnyScreenDefinition[];
  /** Module paths, relative to the screens directory, in the generated index. */
  files: string[];
}

let cached: ScreenRegistry | null = null;

export function loadScreenRegistrySync(opts: { fresh?: boolean } = {}): ScreenRegistry {
  if (cached && !opts.fresh) return cached;
  const screens: AnyScreenDefinition[] = [];
  const files: string[] = [];
  const seenIds = new Map<string, string>();
  const seenRoutes = new Map<string, string>();
  const problems: string[] = [];

  for (const [path, screen] of Object.entries(SCREEN_MODULES)) {
    files.push(path);
    if (!screen || typeof screen !== 'object' || !('id' in screen)) {
      problems.push(`${path}: does not export a ScreenDefinition named 'screen'.`);
      continue;
    }
    const def = screen as AnyScreenDefinition;
    const dupId = seenIds.get(def.id);
    if (dupId) problems.push(`Duplicate screen id ${def.id} in ${path} and ${dupId}.`);
    else seenIds.set(def.id, path);

    const routeKey = `${def.surface}:${def.route}`;
    const dupRoute = seenRoutes.get(routeKey);
    if (dupRoute) problems.push(`Duplicate ${def.surface} route ${def.route} in ${path} and ${dupRoute}.`);
    else seenRoutes.set(routeKey, path);

    screens.push(def);
  }

  if (problems.length) throw new Error(`Screen registry is invalid:\n  - ${problems.join('\n  - ')}`);
  screens.sort((a, b) => String(a.id).localeCompare(String(b.id)));
  cached = { screens, files };
  return cached;
}

export async function loadScreenRegistry(opts: { fresh?: boolean } = {}): Promise<ScreenRegistry> {
  return loadScreenRegistrySync(opts);
}

export function clearScreenRegistryCache(): void {
  cached = null;
}
